import { Box, Link, Text as CKText } from '@chakra-ui/react';
import { Colors, Icon, Text } from '@mercator-gcp/grout';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import Notes from 'components/widgets/Notes';
import CrmRecordLink from 'components/widgets/ProjectDetails/CrmRecordLink';
import PushToCrm from 'components/widgets/ProjectDetails/PushToCrm';
import SaveProject from 'components/widgets/SaveProject';
import SharedProjectListsOnPdp from 'components/widgets/SaveProject/SharedProjectListsOnPdp';
import { useToast } from 'hooks/useToast';
import {
  trackDownloadProject,
  trackProjectNote,
  trackShareProject,
} from 'utils/analytics';
import {
  ProjectDetailsPageViewSections,
  ProjectDetailsPageViewSubSections,
} from 'utils/analytics/constants';
import { printDDPUrl, printPDPUrl } from 'utils/navigation';

const shortcutText = {
  color: 'brand.army-green',
  letterSpacing: '-0.01em',
  textDecoration: 'none',
};

const shortcutContainer = {
  display: 'inline-flex',
  gap: 'padding_4',
};

const shortcutColumn = {
  gap: '8px',
  mb: 'fortyEight',
  '.chakra-link': {
    textDecoration: 'none',
    _hover: {
      textDecoration: 'underline',
    },
  },
};

const projectNotes = {
  marginTop: 'padding_24',
  marginBottom: 'padding_8',
};

const listItemContentStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  borderRadius: '4px',
  gap: 'padding_8',
  _hover: {
    backgroundColor: Colors.ArmyGreen100,
  },
  width: '100%',
  padding: 'padding_8',
};

const LABELS = {
  DOWNLOAD_AS_PDF_BUTTON: 'Download as PDF',
  COPY_SHARE_LINK_BUTTON: 'Copy Share Link',
};

type TMenuProps = {
  address?: string;
  city?: string | null;
  projectId?: number;
  projectSlug?: string;
  documentId?: string;
};

const Menu = ({
  address = '',
  city = '',
  projectId,
  projectSlug,
  documentId,
}: TMenuProps) => {
  const { renderToast } = useToast();

  const createGoogleSearchURL = (news = false) => {
    const sanitizedAddress = address?.replace(/^#/, '');
    let url = `https://www.google.ca/search?q=${sanitizedAddress}+${city}`;

    if (news) {
      url += '&tbm=nws';
    }

    return url;
  };

  const onClickDownloadAsPDF = () => {
    trackDownloadProject({
      projectId: projectSlug || documentId || '',
      clickText: LABELS.DOWNLOAD_AS_PDF_BUTTON,
      sectionName: ProjectDetailsPageViewSections.PROJECT_DETAILS,
      subSectionName: ProjectDetailsPageViewSubSections.MENU,
    });
    let urlPath = '';
    if (documentId) {
      urlPath = printDDPUrl(documentId);
    } else if (projectSlug) {
      urlPath = printPDPUrl(projectSlug);
    }
    window.open(
      urlPath,
      '_blank',
      'popup=yes,location=yes,height=1000,width=1200,scrollbars=yes,status=yes',
    );
  };

  const onClickCopyShareLink = async () => {
    try {
      // Copy the current URL to clipboard
      await navigator.clipboard.writeText(window.location.href);

      // Show success toast
      renderToast({
        toastTitle: 'Link copied to clipboard',
        status: 'success',
        title: 'Success',
        label: 'Project link has been copied to your clipboard',
      });

      // Track analytics event
      trackShareProject({
        projectId: projectSlug || documentId || '',
        clickText: LABELS.COPY_SHARE_LINK_BUTTON,
        sectionName: ProjectDetailsPageViewSections.PROJECT_DETAILS,
        subSectionName: ProjectDetailsPageViewSubSections.MENU,
      });
    } catch (_) {
      // Show error toast if copy fails
      renderToast({
        toastTitle: 'Failed to copy link',
        status: 'error',
        title: 'Error',
        label: 'Unable to copy link to clipboard. Please try again.',
      });
    }
  };

  if (!projectSlug && !documentId) {
    return null;
  }

  const googleSearchURL = createGoogleSearchURL();

  const googleNewsURL = createGoogleSearchURL(true);

  return (
    <>
      <FlexColumn sx={shortcutColumn}>
        <Text variant="smallLabel" color={Colors.TextQuiet}>
          Save project
        </Text>
        <SaveProject projectSlug={projectSlug} documentId={documentId} />
        <FlexRow
          onClick={onClickDownloadAsPDF}
          cursor="pointer"
          sx={listItemContentStyle}
        >
          <Icon name={Icon.Name.Download} fill={Colors.ForestGreen900} />
          <Text.BodyBold>{LABELS.DOWNLOAD_AS_PDF_BUTTON}</Text.BodyBold>
        </FlexRow>
        <FlexRow
          onClick={onClickCopyShareLink}
          cursor="pointer"
          sx={listItemContentStyle}
        >
          <Icon name={Icon.Name.Link} fill={Colors.ForestGreen900} />
          <Text.BodyBold>{LABELS.COPY_SHARE_LINK_BUTTON}</Text.BodyBold>
        </FlexRow>
        {projectSlug && (
          <SharedProjectListsOnPdp projectSlug={projectSlug} documentId={documentId} />
        )}
        <PushToCrm
          resourceType={documentId ? 'document' : 'project'}
          resourceId={(documentId ?? projectSlug) as string}
        />
        <CrmRecordLink
          resourceType={documentId ? 'document' : 'project'}
          resourceId={(documentId ?? projectSlug) as string}
        />
      </FlexColumn>
      <FlexColumn sx={shortcutColumn}>
        <Text variant="smallLabel" color={Colors.TextQuiet}>
          Search this address
        </Text>
        <Link href={googleSearchURL} target="_blank" sx={shortcutContainer}>
          <CKText sx={shortcutText}>Google Search</CKText>
          <Icon name={Icon.Name.ArrowDiagonal} fill={Colors.ForestGreen900} />
        </Link>
        <Link href={googleNewsURL} target="_blank" sx={shortcutContainer}>
          <CKText sx={shortcutText}>Google News</CKText>
          <Icon name={Icon.Name.ArrowDiagonal} fill={Colors.ForestGreen900} />
        </Link>
        {(projectId ?? documentId) && (
          <Box sx={projectNotes}>
            <Notes
              targetId={(projectId ?? documentId) as number | string}
              trackingData={{
                sectionName: ProjectDetailsPageViewSections.PROJECT_DETAILS,
                subSectionName: ProjectDetailsPageViewSubSections.MENU,
                onTrack: trackProjectNote,
              }}
            />
          </Box>
        )}
      </FlexColumn>
    </>
  );
};

export default Menu;
